import React from "react";

import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  TLResizeInfo,
} from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { HOVER_EFFECT } from "./constants";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { defaultScale } from "../constants";

type OvalShapeType = TLBaseShape<
  "customOval",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    strokeAlign?: "inside" | "center" | "outside";
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
  }
>;

export class CustomOvalUtil extends BaseBoxShapeUtil<OvalShapeType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: any) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customOval" as const;
  static override props: RecordProps<OvalShapeType> = {
    w: T.number,
    h: T.number,
    fill: T.string,
    outlineColor: T.string,
    dash: T.any,
    transparency: T.number,
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.optional(T.number),
    filterBlur: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any,
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    scale: T.optional(T.number),
  };

  getDefaultProps(): OvalShapeType["props"] {
    return {
      w: 60,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: OvalShapeType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  // Utility function to create a rounded oval path with parallel sides
  getOvalPath(w: number, h: number): string {
    const radius = Math.min(w, h) / 2;
    return `
      M${radius},0 
      H${w - radius} 
      Q${w},0 ${w},${radius} 
      V${h - radius} 
      Q${w},${h} ${w - radius},${h} 
      H${radius} 
      Q0,${h} 0,${h - radius} 
      V${radius} 
      Q0,0 ${radius},0
    `
      .replace(/\s+/g, " ")
      .trim();
  }

  hideSelectionBoundsFg(_shape: OvalShapeType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: OvalShapeType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: OvalShapeType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: OvalShapeType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: OvalShapeType, info: TLResizeInfo<OvalShapeType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: OvalShapeType) {
    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = shape.props;

    // Generate the SVG path for the rounded oval with parallel sides
    const pathData = this.getOvalPath(w, h);

    const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
    const innerShadowFilterId = `inner-shadow-${shape.id}`; // Unique ID for the inner shadow filter
    const dashArray = strokeDasharray || getDashArray(dash);

    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }
  addDefaultColorMix(shape: OvalShapeType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: OvalShapeType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: OvalShapeType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: OvalShapeType) {
    const {
      h,
      w,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
      strokeWidth = 0,
    } = shape.props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const pathData = this.getOvalPath(w, h);
    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={pathData}
        transform={`translate(${isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0}, ${
          isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0
        })`}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}
